import React, { useState } from 'react';
import { CheckCircle2, Circle, ArrowRight, Clock, FileText, Lightbulb, ChevronDown, ChevronUp, Map } from 'lucide-react';
import { getRoadmap, getCurrentStepIndex, type RoadmapStep } from '../../config/roadmaps';

interface ProductRoadmapProps {
  productCode: string;
  currentStatus: string;
  accentColor?: string; // tailwind color name e.g. 'blue', 'emerald'
}

export const ProductRoadmap: React.FC<ProductRoadmapProps> = ({ productCode, currentStatus, accentColor = 'blue' }) => {
  const [expandedStep, setExpandedStep] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);

  const roadmap = getRoadmap(productCode);
  if (!roadmap) return null;

  const currentIdx = getCurrentStepIndex(productCode, currentStatus);
  const isTerminal = roadmap.terminalStatuses.includes(currentStatus);

  // Filter out terminal "rejected/cancelled" from main flow unless it's the current status
  const mainSteps = roadmap.steps.filter(s =>
    !roadmap.terminalStatuses.includes(s.id) || s.id === currentStatus
  );

  // Collapse if too many steps — show context window around current
  const VISIBLE_WINDOW = 5;
  const shouldCollapse = mainSteps.length > VISIBLE_WINDOW + 1 && !showAll;
  let visibleSteps = mainSteps;
  if (shouldCollapse) {
    const start = Math.max(0, currentIdx - 2);
    const end = Math.min(mainSteps.length, start + VISIBLE_WINDOW);
    visibleSteps = mainSteps.slice(start, end);
  }

  const getStepState = (step: RoadmapStep): 'done' | 'current' | 'pending' | 'terminal' => {
    if (step.id === currentStatus) {
      return isTerminal ? 'terminal' : 'current';
    }
    const stepIdx = mainSteps.findIndex(s => s.id === step.id);
    if (currentIdx >= 0 && stepIdx < currentIdx) return 'done';
    return 'pending';
  };

  const stateColors: Record<string, string> = {
    done: 'text-emerald-600',
    current: `text-${accentColor}-600`,
    pending: 'text-gray-300',
    terminal: currentStatus.includes('odrzuć') || currentStatus.includes('anulowa') ? 'text-red-500' : 'text-emerald-600',
  };

  const stateBg: Record<string, string> = {
    done: 'bg-emerald-50 border-emerald-200',
    current: `bg-${accentColor}-50 border-${accentColor}-300`,
    pending: 'bg-gray-50 border-gray-200',
    terminal: currentStatus.includes('odrzuć') || currentStatus.includes('anulowa') ? 'bg-red-50 border-red-200' : 'bg-emerald-50 border-emerald-200',
  };

  const completedCount = currentIdx >= 0 ? currentIdx + (isTerminal ? mainSteps.length : 0) : 0;
  const totalNonTerminal = roadmap.steps.filter(s => !roadmap.terminalStatuses.includes(s.id)).length;
  const progressPct = isTerminal && !currentStatus.includes('odrzuć')
    ? 100
    : totalNonTerminal > 0 ? Math.round((currentIdx / (totalNonTerminal - 1)) * 100) : 0;

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-3">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <Map size={14} className={`text-${accentColor}-500`} />
          <span className="text-xs font-semibold text-gray-700">Mapa procesu</span>
          <span className="text-[10px] text-gray-400">{mainSteps.length} etapów</span>
        </div>
        <span className={`text-[10px] font-bold ${progressPct === 100 ? 'text-emerald-600' : `text-${accentColor}-600`}`}>
          {progressPct}%
        </span>
      </div>

      {/* Progress bar */}
      <div className="w-full h-1.5 bg-gray-100 rounded-full mb-3 overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${progressPct === 100 ? 'bg-emerald-500' : `bg-${accentColor}-500`}`}
          style={{ width: `${Math.max(progressPct, 3)}%` }}
        />
      </div>

      {/* Steps */}
      <div className="space-y-1">
        {shouldCollapse && currentIdx > 2 && (
          <button
            onClick={() => setShowAll(true)}
            className="text-[10px] text-gray-400 hover:text-gray-600 pl-6 mb-0.5"
          >
            ... {currentIdx - 2} wcześniejszych etapów
          </button>
        )}

        {visibleSteps.map((step, i) => {
          const state = getStepState(step);
          const isExpanded = expandedStep === step.id;
          const isCurrent = state === 'current';

          return (
            <div key={step.id}>
              <button
                onClick={() => setExpandedStep(isExpanded ? null : step.id)}
                className={`w-full flex items-start gap-2 p-1.5 rounded-md text-left transition-all hover:bg-gray-50 ${isCurrent ? 'bg-blue-50/50' : ''}`}
              >
                {/* Icon */}
                <div className="mt-0.5 shrink-0">
                  {state === 'done' ? (
                    <CheckCircle2 size={14} className="text-emerald-500" />
                  ) : state === 'current' ? (
                    <div className={`w-3.5 h-3.5 rounded-full border-2 border-${accentColor}-500 bg-${accentColor}-500 flex items-center justify-center`}>
                      <div className="w-1.5 h-1.5 rounded-full bg-white" />
                    </div>
                  ) : state === 'terminal' ? (
                    <CheckCircle2 size={14} className={currentStatus.includes('odrzuć') || currentStatus.includes('anulowa') ? 'text-red-500' : 'text-emerald-500'} />
                  ) : (
                    <Circle size={14} className="text-gray-300" />
                  )}
                </div>

                {/* Label */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1">
                    <span className={`text-xs font-medium truncate ${
                      state === 'done' ? 'text-gray-500 line-through' :
                      state === 'current' ? `text-${accentColor}-700 font-semibold` :
                      state === 'terminal' ? (currentStatus.includes('odrzuć') ? 'text-red-700 font-semibold' : 'text-emerald-700 font-semibold') :
                      'text-gray-400'
                    }`}>
                      {step.label}
                    </span>
                    {step.estimatedDays && state === 'current' && (
                      <span className="text-[9px] text-gray-400 flex items-center gap-0.5 shrink-0">
                        <Clock size={8} />{step.estimatedDays}
                      </span>
                    )}
                  </div>
                  {isCurrent && !isExpanded && (
                    <p className="text-[10px] text-gray-500 truncate mt-0.5">{step.description}</p>
                  )}
                </div>

                {/* Expand indicator */}
                <div className="shrink-0 mt-0.5">
                  {isExpanded ? <ChevronUp size={10} className="text-gray-400" /> : <ChevronDown size={10} className="text-gray-300" />}
                </div>
              </button>

              {/* Expanded details */}
              {isExpanded && (
                <div className={`ml-6 mr-1 mb-1.5 p-2 rounded border text-[10px] ${stateBg[state]}`}>
                  <p className="text-gray-600 mb-1.5">{step.description}</p>
                  {step.requirements && step.requirements.length > 0 && (
                    <div className="mb-1.5">
                      <span className="font-semibold text-gray-500 flex items-center gap-0.5 mb-0.5">
                        <FileText size={9} /> Wymagane:
                      </span>
                      <ul className="list-disc list-inside text-gray-500 space-y-0.5 ml-1">
                        {step.requirements.map(r => <li key={r}>{r}</li>)}
                      </ul>
                    </div>
                  )}
                  {step.estimatedDays && (
                    <div className="flex items-center gap-1 text-gray-500 mb-1">
                      <Clock size={9} /> Szacowany czas: {step.estimatedDays}
                    </div>
                  )}
                  {step.operatorTip && (
                    <div className="flex items-start gap-1 text-amber-700 bg-amber-50 rounded p-1 mt-1">
                      <Lightbulb size={9} className="mt-0.5 shrink-0" />
                      <span>{step.operatorTip}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {shouldCollapse && (
          <button
            onClick={() => setShowAll(true)}
            className="text-[10px] text-gray-400 hover:text-gray-600 pl-6"
          >
            ... jeszcze {mainSteps.length - visibleSteps.length - (currentIdx > 2 ? currentIdx - 2 : 0)} etapów
          </button>
        )}
        {showAll && mainSteps.length > VISIBLE_WINDOW + 1 && (
          <button
            onClick={() => setShowAll(false)}
            className="text-[10px] text-gray-400 hover:text-gray-600 pl-6"
          >
            Zwiń
          </button>
        )}
      </div>
    </div>
  );
};
